import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, fileUrl } from "@/lib/api";

function GalleryItem({ item, index }) {
  const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];
  const heights = ["h-[420px]", "h-[420px]", "h-[520px]", "h-[520px]"];
  return (
    <motion.figure
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`gallery-item-${index}`}
      className={`group relative overflow-hidden col-span-12 ${spans[index % 4]}`}
    >
      <div className={`relative w-full ${heights[index % 4]} overflow-hidden`}>
        <img
          src={fileUrl(item.storage_path)}
          alt={item.title}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-[filter,transform] duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-charcoal/30 group-hover:bg-charcoal/0 transition-colors duration-500" />
        <div className="absolute inset-0 ring-1 ring-inset ring-charcoal/20" />
      </div>
      <figcaption className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-500">
        <div>
          <p className="font-serif text-cream text-2xl">{item.title}</p>
          {item.location && (
            <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-cream/80 mt-1">{item.location}</p>
          )}
        </div>
      </figcaption>
    </motion.figure>
  );
}

export function RecentJobs() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/gallery")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="work" data-testid="work-section" className="bg-charcoal py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-terracotta" />
              <span className="font-sans uppercase tracking-[0.3em] text-xs text-cream/50">Recent Work</span>
            </div>
            <h2 className="font-serif text-cream text-5xl md:text-6xl tracking-tight leading-[0.95]">
              A few pieces we're <span className="italic text-terracotta">proud</span> of.
            </h2>
          </div>
          <a
            href="#contact"
            data-testid="work-cta"
            className="font-sans uppercase tracking-[0.2em] text-xs text-cream border-b border-cream/40 pb-1 hover:text-terracotta hover:border-terracotta transition-colors duration-300 shrink-0"
          >
            Start your project
          </a>
        </div>

        {loaded && items.length === 0 ? (
          <div
            data-testid="gallery-empty"
            className="border border-cream/15 py-24 text-center"
          >
            <p className="font-serif italic text-cream/60 text-2xl">Fresh work coming soon.</p>
            <p className="font-sans text-cream/40 text-sm mt-3 uppercase tracking-[0.2em]">
              Photos of recent jobs will appear here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {items.map((item, i) => (
              <GalleryItem key={item.id} item={item} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
